import { cn } from '@lib/cn';
import { Badge } from '@shared/ui';
import { useId, useState } from 'react';

import { CATEGORY_LABEL, CATEGORY_TONE } from '../model/final-category';
import { rawText } from '../model/final-report-export';

import type { FinalReportRow } from '../model/final-report';
import type { NotMigratedGroup } from '../model/report-sections';
import type { ColumnDef } from '../schemas/bulk-migration';

/**
 * The clients that were not migrated, as a compact list grouped by why — not
 * sheets: there is no run to show. Each keeps the raw Salesforce values the
 * analysis read, folded on screen and unfolded on paper: they are the audit
 * trail for a client wrongly reported as not migratable.
 */

function SalesforceValues({
    row,
    columns,
}: {
    row: FinalReportRow;
    columns: readonly ColumnDef[];
}) {
    const [open, setOpen] = useState(false);
    const panelId = useId();
    if (row.records.length === 0 || columns.length === 0) return null;
    return (
        <div className="mt-1.5">
            <button
                type="button"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => {
                    setOpen((current) => !current);
                }}
                className="text-[12.5px] font-medium text-accent-text hover:underline print:hidden"
            >
                {open ? 'Hide Salesforce values' : 'Salesforce values'}
            </button>
            <div
                id={panelId}
                className={cn(open ? 'block' : 'hidden', 'mt-2 grid gap-3 print:block')}
            >
                {row.records.map((record) => (
                    <dl
                        key={record.sourceId}
                        className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 rounded-lg bg-surface-sunken px-3 py-2.5 text-[12.5px] print:bg-transparent"
                    >
                        <dt className="text-content-muted">Record</dt>
                        <dd className="min-w-0 [overflow-wrap:anywhere]">
                            {record.title}{' '}
                            <span className="font-mono text-[11.5px] text-content-muted">
                                {record.sourceId}
                            </span>
                        </dd>
                        {columns.map((column) => (
                            <div key={column.key} className="contents">
                                <dt className="text-content-muted">{column.label}</dt>
                                <dd className="min-w-0 [overflow-wrap:anywhere]">
                                    {rawText(record.columns[column.key]) || '—'}
                                </dd>
                            </div>
                        ))}
                    </dl>
                ))}
            </div>
        </div>
    );
}

export function NotMigratedSection({
    groups,
    columns,
}: {
    groups: readonly NotMigratedGroup[];
    columns: readonly ColumnDef[];
}) {
    return (
        <section aria-labelledby="not-migrated" className="grid gap-4">
            <h2
                id="not-migrated"
                className="text-[20px] font-semibold tracking-tight text-content print:break-after-avoid"
            >
                Not migrated
            </h2>
            {groups.map((group) => (
                <div key={group.reason} className="grid gap-2">
                    <h3 className="text-[12px] font-semibold uppercase tracking-wider text-content-muted print:break-after-avoid">
                        {group.title} · {group.rows.length}
                    </h3>
                    <ul className="divide-y divide-line-subtle overflow-hidden rounded-xl border border-line bg-surface">
                        {group.rows.map((row) => (
                            <li key={row.tenant.tenantId} className="break-inside-avoid px-4 py-3">
                                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                                    <span className="font-semibold text-content">
                                        {row.tenant.name}
                                    </span>
                                    {row.tenant.slug && (
                                        <span className="font-mono text-[12px] text-content-muted">
                                            {row.tenant.slug}
                                        </span>
                                    )}
                                    {row.tenant.isSandbox && <Badge tone="dev">Sandbox</Badge>}
                                    {row.launched && <Badge tone="neutral">Launched</Badge>}
                                    <Badge tone={CATEGORY_TONE[row.category]}>
                                        {row.reason === null ? (
                                            CATEGORY_LABEL[row.category]
                                        ) : (
                                            <span className="font-mono">{row.reason}</span>
                                        )}
                                    </Badge>
                                </div>
                                {row.message && (
                                    <p className="mt-1 max-w-[80ch] text-[13px] text-content-muted">
                                        {row.message}
                                    </p>
                                )}
                                <SalesforceValues row={row} columns={columns} />
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </section>
    );
}
